/**
 * Calls to the account and billing endpoints.
 */

const isDevelopment =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

export const API_URL: string =
  import.meta.env.VITE_API_URL ||
  (isDevelopment
    ? 'http://localhost:5000'
    : 'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com');

export type Plan = 'none' | 'standard' | 'pro';
export type PaidPlan = Exclude<Plan, 'none'>;

export interface AccountSummary {
  email: string;
  plan: Plan;
  subscription_status: string;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  has_billing_account: boolean;
}

export interface AccountSession {
  token: string;
  expires_at: number;
  account: AccountSummary;
}

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: string;
  token?: string | null;
  body?: unknown;
}

export const accountRequest = async <T>(
  path: string,
  { method = 'GET', token, body }: RequestOptions = {}
): Promise<T> => {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(
      "Couldn't reach the server. Check your connection and try again.",
      0
    );
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      typeof data?.detail === 'string'
        ? data.detail
        : 'Something went wrong. Please try again.';
    throw new ApiError(detail, response.status, data?.code);
  }
  return data as T;
};

export const redirectTo = (url: string): void => {
  window.location.assign(url);
};
